/**
 * Color Detection — Daisy Connected Shop
 * Dynamically adjusts header and scroll-indicator colors based on the
 * brightness of the content behind them.
 *
 * Ported from the WordPress Connected_v01 theme's logo-detection
 * and burger-detection system. Uses elementFromPoint + computed
 * background-color walking + optional canvas image sampling to
 * determine if an element sits over a dark or light region.
 *
 * Adds/removes `.header--over-dark` on `.site-header`.
 * Adds/removes `.over-dark` on `.hero-scroll-indicator`.
 */
(function () {
  'use strict';

  var THROTTLE_MS = 50;
  var BRIGHTNESS_THRESHOLD = 80; // 0-255; below = dark
  var MAX_DOM_WALK = 10;

  /* ---- Brightness utilities ---- */

  function getBrightnessFromColor(colorString) {
    var rgb = colorString.match(/\d+/g);
    if (!rgb || rgb.length < 3) return null;
    return 0.299 * parseInt(rgb[0], 10) +
           0.587 * parseInt(rgb[1], 10) +
           0.114 * parseInt(rgb[2], 10);
  }

  function isTransparentColor(colorString) {
    if (!colorString || colorString === 'transparent') return true;
    var rgba = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (rgba && rgba[4] !== undefined) {
      return parseFloat(rgba[4]) === 0;
    }
    return false;
  }

  function sampleImage(img, rect) {
    try {
      if (!img.complete || !img.naturalWidth) return null;
      var w = Math.min(100, rect.width);
      var h = Math.min(100, rect.height);
      var canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      var ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight, 0, 0, w, h);
      var data = ctx.getImageData(0, 0, w, h).data;
      var total = 0;
      var count = 0;
      for (var i = 0; i < data.length; i += 16) {
        if (data[i + 3] > 128) {
          total += 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          count++;
        }
      }
      return count > 0 ? total / count : null;
    } catch (e) {
      return null;
    }
  }

  /* ---- Detection core ---- */

  /**
   * Detect brightness at a specific point, hiding given elements first.
   * @param {number} x - X coordinate to sample
   * @param {number} y - Y coordinate to sample
   * @param {Element[]} hideElements - Elements to hide during detection
   * @param {DOMRect} sampleRect - Rectangle for image sampling dimensions
   * @returns {number|null} Brightness 0-255 or null
   */
  function getBrightnessAtPoint(x, y, hideElements, sampleRect) {
    var decorative = document.getElementById('decorative-background');

    // Save and hide
    var saved = [];
    for (var i = 0; i < hideElements.length; i++) {
      saved.push(hideElements[i].style.visibility);
      hideElements[i].style.visibility = 'hidden';
    }
    var prevDecVis = decorative ? decorative.style.visibility : undefined;
    if (decorative) decorative.style.visibility = 'hidden';

    var behind = document.elementFromPoint(x, y);

    // Restore
    for (var j = 0; j < hideElements.length; j++) {
      hideElements[j].style.visibility = saved[j];
    }
    if (decorative && prevDecVis !== undefined) {
      decorative.style.visibility = prevDecVis;
    }

    if (!behind) return null;

    // Priority 1 — data-bg-mode override
    var el = behind;
    while (el && el !== document.body) {
      if (el.hasAttribute && el.hasAttribute('data-bg-mode')) {
        var mode = el.getAttribute('data-bg-mode');
        if (mode === 'dark') return 0;
        if (mode === 'light') return 255;
      }
      el = el.parentElement;
    }

    // Priority 2 — image sampling
    var img = behind.tagName === 'IMG' ? behind : behind.querySelector('img');
    if (img) {
      var brightness = sampleImage(img, sampleRect);
      if (brightness !== null) return brightness;
    }

    // Priority 3 — walk up DOM for first non-transparent background
    var current = behind;
    var attempts = 0;
    while (current && attempts < MAX_DOM_WALK) {
      var bgChild = current.querySelector('[class*="__background"], [class*="__overlay"]');
      if (bgChild) {
        var childBg = window.getComputedStyle(bgChild).backgroundColor;
        if (!isTransparentColor(childBg)) {
          return getBrightnessFromColor(childBg);
        }
      }

      var bg = window.getComputedStyle(current).backgroundColor;
      if (!isTransparentColor(bg)) {
        return getBrightnessFromColor(bg);
      }

      current = current.parentElement;
      attempts++;
    }

    return null;
  }

  /* ---- Throttle ---- */

  function throttle(fn, delay) {
    var last = 0;
    var timer = null;
    return function () {
      var now = Date.now();
      if (timer) clearTimeout(timer);
      if (now - last >= delay) {
        fn();
        last = now;
      } else {
        timer = setTimeout(function () {
          fn();
          last = Date.now();
        }, delay - (now - last));
      }
    };
  }

  /* ---- Init ---- */

  function init() {
    var header = document.querySelector('.site-header');
    if (!header) return;

    var scrollIndicator = document.querySelector('.hero-scroll-indicator');

    function update() {
      // --- Header detection ---
      if (header.classList.contains('scrolled')) {
        header.classList.remove('header--over-dark');
      } else {
        var headerRect = header.getBoundingClientRect();
        var hx = headerRect.left + headerRect.width / 2;
        var hy = headerRect.top + headerRect.height / 2;
        var headerBrightness = getBrightnessAtPoint(hx, hy, [header], headerRect);

        if (headerBrightness === null || headerBrightness < BRIGHTNESS_THRESHOLD) {
          header.classList.add('header--over-dark');
        } else {
          header.classList.remove('header--over-dark');
        }
      }

      // --- Scroll indicator detection ---
      if (scrollIndicator) {
        var indRect = scrollIndicator.getBoundingClientRect();
        // Only detect when indicator is in the viewport
        if (indRect.top >= 0 && indRect.top < window.innerHeight) {
          var ix = indRect.left + indRect.width / 2;
          var iy = indRect.top + indRect.height / 2;
          var indBrightness = getBrightnessAtPoint(ix, iy, [header, scrollIndicator], indRect);

          if (indBrightness === null || indBrightness < BRIGHTNESS_THRESHOLD) {
            scrollIndicator.classList.add('over-dark');
          } else {
            scrollIndicator.classList.remove('over-dark');
          }
        }
      }
    }

    var throttledUpdate = throttle(update, THROTTLE_MS);

    window.addEventListener('scroll', throttledUpdate, { passive: true });
    window.addEventListener('resize', throttledUpdate, { passive: true });

    update();
    setTimeout(update, 200);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
